<template>
    <div id="main">
        <div class="page" v-show="!rotaAninhada">
            <div class="vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t("agro-fenologia") }}
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{
                                        $t("agro")
                                    }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{
                                        $t("agro-fenologia")
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            v-if="permissao.create"
                            class="save-btn"
                            type="button"
                            tag="button"
                            @click="
                                $router.push({ name: 'agro-fenologia-new' })
                            "
                        >
                            {{ $t("adicionar") }}
                        </button>
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-5">
                        <search-bar
                            :onSearch="debouncedOnSearch"
                            tooltipLabel="info-pesquisa-fenologia"
                        />
                    </div>
                    <div class="column is-4">
                        <vss-select
                            ref="empresaInput"
                            title="empresa"
                            placeholder="selecione"
                            :options="empresaSelect"
                            v-model="empresaSelected"
                            :height="40"
                            :loading="empresasLoading"
                            @search="carregarEmpresas"
                            multiple
                        />
                    </div>
                    <div class="column is-3">
                        <vss-select
                            ref="safraInput"
                            title="safra"
                            placeholder="selecione"
                            :options="safraSelect"
                            @search="loadSafras"
                            v-model="safraSelected"
                            :height="40"
                            clearable
                            multiple
                            searchable
                            :closeOnSelect="false"
                        />
                    </div>
                </div>

                <!-- FILTROS EXTRAS -->
                <div class="filtros-extras">
                    <FiltroFenologia
                        @update-filtro="updateFiltro"
                        ref="filtroRef"
                        :limparFiltros="limparFiltros"
                        @filtrosLimpos="limparFiltros = $event"
                    />
                </div>

                <!-- BOTÕES -->
                <div class="columns mt-2 mb-1">
                    <div class="column is-6 has-text-centered">
                        <button
                            class="delete-btn pl-4 pr-4"
                            @click="confirmCleanFilters"
                        >
                            {{ $t("limpar-filtros") }}
                        </button>
                    </div>
                    <div class="column is-6 has-text-centered">
                        <button
                            class="save-btn pl-4 pr-4"
                            @click="carregarDados()"
                        >
                            {{ $t("pesquisar") }}
                        </button>
                    </div>
                </div>

                <!-- LISTAGEM -->
                <div class="registry-wrapper">
                    <container-loader
                        v-if="isLoading && !$store.getters.isLoading"
                    />
                    <empty-table-message v-if="isEmpty" />
                    <b-table
                        v-else
                        :data="fenologias"
                        class="data-table"
                        @sort="sortTable"
                        backend-sorting
                        striped
                    >
                        <b-table-column
                            v-for="(f, i) in fields"
                            :key="i"
                            :field="f.key"
                            :label="f.key != 'empresa' ? $t(f.key) : ''"
                            :width="f.width"
                            :sortable="f.sortable"
                            :custom-sort="f.sortFn"
                        >
                            <!-- Cabeçalho da listagem -->
                            <template slot="header">
                                <span v-if="f.key == 'cliente'">
                                    {{ $t("cliente") }}
                                    <div class="table-sep" />
                                    {{ $t("data-hora") }}
                                </span>
                                <span v-else-if="f.key == 'cultura'">
                                    {{ $t("cultura") }}
                                    <div class="table-sep" />
                                    {{ $t("safra") }}
                                </span>
                                <span v-else-if="f.key == 'propriedade'">
                                    {{ $t("propriedade") }}
                                    <div class="table-sep" />
                                    {{ $t("usuario") }}
                                </span>
                                <span v-else-if="f.key == 'situacao'">
                                    {{ $t("situacao") }}
                                    <div class="table-sep" />
                                    {{ $t("origem") }}
                                </span>
                                <span v-else-if="f.key == 'codigo'">
                                    {{ $t("codigo") }}
                                    <div class="table-sep" />
                                    {{ $t("empresa") }}
                                </span>
                            </template>

                            <!-- Dados da listagem -->
                            <template v-slot="props">
                                <div v-if="f.key == 'cliente'">
                                    <router-link
                                        :to="{
                                            name: 'agro-fenologia-edit',
                                            params: { id: props.row.uuid },
                                        }"
                                    >
                                        <strong>{{ props.row.cliente }}</strong>
                                    </router-link>
                                    <div class="table-sep" />
                                    {{
                                        $moment(props.row.datahora).format(
                                            "L LT"
                                        )
                                    }}
                                </div>
                                <div v-else-if="f.key == 'cultura'">
                                    <strong>{{ props.row.cultura }}</strong>
                                    <div class="table-sep" />
                                    {{ props.row.safra }}
                                </div>
                                <div v-else-if="f.key == 'propriedade'">
                                    <strong>{{ props.row.propriedade }}</strong>
                                    <div class="table-sep" />
                                    {{ props.row.usuario }}
                                </div>
                                <div v-else-if="f.key == 'codigo'">
                                    {{ props.row[f.key] }}
                                    <div class="table-sep" />
                                    <strong>{{ props.row.empresa.nome }}</strong>
                                </div>
                                <div v-else-if="f.key == 'situacao'">
                                    <span
                                        class="tag"
                                        :class="{
                                            'is-warning':
                                                props.row.situacao == 'E',
                                            'is-orange':
                                                props.row.situacao == 'criacao',
                                            'is-success':
                                                props.row.situacao ==
                                                'finalizada',
                                            'is-danger':
                                                props.row.situacao == 'inativo',
                                        }"
                                    >
                                        {{ $t(props.row.situacao) }}
                                    </span>
                                    <div class="has-text-centered mt-2">
                                        {{ props.row.origem }}
                                    </div>
                                </div>
                            </template>
                        </b-table-column>
                    </b-table>
                    <PaginationViewer
                        class="mb-0"
                        :resetPagination="pagination.resetPagination"
                        :totalItems="pagination.totalRows"
                        :perPageOptions="pagination.perPageOptions"
                        :optionStarted="pagination.optionStarted"
                        @change="updatePagination"
                    />
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import AgroFenologiaService from "@/services/agro/fenologia/AgroFenologiaService";
import SafraService from "@/services/agro/SafraService";
import EmpresaService from "@/services/vendas/empresa/EmpresaService";
import ContainerLoader from "@/components/content/loader/ContainerLoader";
import FiltroFenologia from "@/components/agro/fenologia/FiltroFenologia";

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes("agro-fenologia") && to.name != "agro-fenologia-list") {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        ContainerLoader,
        FiltroFenologia,
    },
    data() {
        return {
            rotaAninhada: false,
            fenologias: [],
            service: new AgroFenologiaService(this.$http, this.$router),
            safraService: new SafraService(this.$http, this.$router),
            empresaService: new EmpresaService(this.$http, this.$router),
            fields: [
                { key: "codigo", width: null, sortable: false, sortFn: null },
                { key: "cliente", width: null, sortable: false, sortFn: null },
                { key: "empresa", width: null, sortable: false, sortFn: null },
                {
                    key: "propriedade",
                    width: null,
                    sortable: false,
                    sortFn: null,
                },
                { key: "cultura", width: null, sortable: false, sortFn: null },
                { key: "situacao", width: null, sortable: false, sortFn: null },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            debouncedOnSearch: null,
            queryFilter: "",
            delete: null,
            permissao: {},
            order: "desc",
            column: "datahora",
            searchQuery: "",
            empresaSelect: [],
            empresaSelected: [],
            safraSelect: [],
            safraSelected: [],
            params: "",
            filtros: {
                idsClientes: [],
                uuidsPropriedades: [],
                uuidsAreas: [],
                uuidsTalhoes: [],
                idsUsuarios: [],
                idsCulturas: [],
                dataInicio: null,
                dataFim: null,
                status: 0,
            },
            limparFiltros: false,
            empresasLoading: false,
        };
    },
    created() {
        this.rotaAninhada = (this.$route.name != 'agro-fenologia-list');

        this.pagination.perPage =
            this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess(
            "agro",
            "agro-fenologia",
            "agro-fenologia"
        );

        if (!this.permissao.read) {
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "agro-fenologia" },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDatas();
            this.carregarDados();
            this.carregarEmpresas("");
            this.loadSafras();
        }
    },
    methods: {
        updateFiltro(filtro) {
            let nome = filtro.nomeFiltro;
            switch (nome) {
                case "cliente":
                    this.filtros.idsClientes = filtro.valor;
                    break;
                case "propriedade":
                    this.filtros.uuidsPropriedades = filtro.valor;
                    break;
                case "area":
                    this.filtros.uuidsAreas = filtro.valor;
                    break;
                case "talhao":
                    this.filtros.uuidsTalhoes = filtro.valor;
                    break;
                case "usuario":
                    this.filtros.idsUsuarios = filtro.valor;
                    break;
                case "cultura":
                    this.filtros.idsCulturas = filtro.valor;
                    break;
                case "inicio":
                    this.filtros.dataInicio = filtro.valor;
                    break;
                case "fim":
                    this.filtros.dataFim = filtro.valor;
                    break;
            }
        },
        carregarDatas() {
            this.filtros.dataInicio = (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 31);
                return trintaDiasAtras;
            })();
            this.filtros.dataFim = new Date();
        },
        limparFiltrosCall() {
            this.empresaSelected = [];
            this.safraSelected = [];
            this.filtros.idsClientes = [];
            this.filtros.uuidsPropriedades = [];
            this.filtros.uuidsAreas = [];
            this.filtros.uuidsTalhoes = [];
            this.filtros.idsUsuarios = [];
            this.filtros.idsCulturas = [];
            this.filtros.dataInicio = (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 31);
                return trintaDiasAtras;
            })();
            this.filtros.dataFim = new Date();
            this.filtros.status = 0;
            this.limparFiltros = true;
            this.carregarDados();
        },
        confirmCleanFilters() {
            this.$root.confirm({
                message: this.$t("msg-confirm-limpar-filtros"),
                onConfirm: () => this.limparFiltrosCall(),
            });
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.queryFilter = this.removeToSearch(value);
            this.pagination.currentPage = 1;
            this.carregarDados();
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;

            this.isLoading = true;

            let filtros = {
                idsEmpresas: this.empresaSelected
                    ? this.empresaSelected.map((e) => e.id)
                    : [],
                idsSafras: this.safraSelected
                    ? this.safraSelected.map((e) => e.id)
                    : [],
                idsClientes: this.filtros.idsClientes
                    ? this.filtros.idsClientes.map((e) => e.idcliente)
                    : [],
                uuidsPropriedades: this.filtros.uuidsPropriedades
                    ? this.filtros.uuidsPropriedades.map((e) => e.uuid)
                    : [],
                uuidsAreas: this.filtros.uuidsAreas
                    ? this.filtros.uuidsAreas.map((e) => e.uuid)
                    : [],
                uuidsTalhoes: this.filtros.uuidsTalhoes
                    ? this.filtros.uuidsTalhoes.map((e) => e.uuid)
                    : [],
                idsCulturas: this.filtros.idsCulturas
                    ? this.filtros.idsCulturas.map((e) => e.id)
                    : [],
                idsUsuarios: this.filtros.idsUsuarios
                    ? this.filtros.idsUsuarios.map((e) => e.id)
                    : [],
                inicio: this.filtros.dataInicio
                    ? this.$moment(this.filtros.dataInicio).format("YYYY-MM-DD")
                    : null,
                fim: this.filtros.dataFim
                    ? this.$moment(this.filtros.dataFim).format("YYYY-MM-DD")
                    : null,
            };

            let params = [this.queryFilter != "" ? this.queryFilter : ".*"];

            this.service
                .findAll(
                    filtros,
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    params
                )
                .then(this.mapItems)
                .then((value) => {
                    this.fenologias = value;
                    this.isEmpty = this.fenologias.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                    console.error("Erro ao carregar as Fenologias.");
                })
                .finally(() => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;

            return body.content.map((m) => {
                let situacao = "";
                if (m.situacao == 4) {
                    situacao = "criacao";
                } else if (m.situacao == 1) {
                    situacao = "finalizada";
                } else {
                    situacao = "inativo";
                }
                return {
                    codigo: m.idAgroFaseFenologicaORegErp,
                    uuid: m.uuid,
                    datahora: m.dataHora,
                    cliente: m.cliente ? m.cliente.nome : "---",
                    cultura: m.cultura ? m.cultura.descricao : "---",
                    propriedade: m.propriedade ? m.propriedade.nome : "---",
                    situacao: situacao,
                    usuario: m.usuario ? m.usuario.nome : "---",
                    safra:
                        m.planosafra && m.planosafra.descricao
                            ? m.planosafra.descricao
                            : "---",
                    origem: m.origem ? m.origem : "---",
                    empresa: m.empresa,
                };
            });
        },
        /*
            @84626
            retornar empresas que estão associadas a um usuário informado. Caso o usuário for admin,
            retorna todas as empresas
        */
        carregarEmpresas(q) {
            this.empresasLoading = true;
            this.empresaService.findList(q)
                .then(r => {
                    if (r.status != 200) {
                        console.error(`[FENOLOGIA] Erro status ${r.status} ao carregar empresas.`);
                        console.error(`[FENOLOGIA] Mensagem: ${r.message}`);
                    } else {
                        this.empresaSelect = r.content.map((e) => {
                        return {
                                id: e.idempresa,
                                label: e.nome,
                            };
                        });
                    }
                })
                .catch(e => {
                    console.error('[FENOLOGIA] Erro ao carregar empresas.', e);
                })
                .finally(_ => {
                    this.empresasLoading = false;
                });
        },
        // Carregar as safras para serem exibidas no filtro por safras
        loadSafras(q) {
            this.isLoading = true;
            this.safraService
                .findAllCombo(q)
                .then(this.mapSafras)
                .then((value) => {
                    this.safraSelect = value;
                })
                .catch((e) => {
                    console.error(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        mapSafras(body) {
            return body.content.map((i) => {
                return {
                    id: i.idsafra,
                    label: i.descricao,
                };
            });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;
    }

    .status-tag {
        margin-top: 10px;
    }

    .datepicker input {
        height: 40px !important;
    }

    .tag {
        width: 100%;
        font-size: 0.75rem;
        -webkit-box-align: center;
        -ms-flex-align: center;
        align-items: center;
        border-radius: 4px;
        display: -webkit-inline-box;
        display: -ms-inline-flexbox;
        display: inline-flex;
        height: 2em;
        -webkit-box-pack: center;
        -ms-flex-pack: center;
        justify-content: center;
        line-height: 1.5;
        padding-left: 0.75em;
        padding-right: 0.75em;
        white-space: nowrap;
        margin-bottom: 2px;
    }

    .is-orange {
        background-color: #ef8419;
        color: #fff;
    }
}
</style>